import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, FormControlLabel,
  Checkbox, FormGroup, Divider, Tooltip, Snackbar, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, ContentCopy, Autorenew, OpenInNew } from '@mui/icons-material';
import api from '../../api/client';

interface StatusBoard {
  id: number; name: string; token: string;
  show_law: boolean; show_fire: boolean; show_ems: boolean;
  agency_ids: number[]; show_address: boolean; show_weather: boolean;
  weather_label: string; weather_lat: number | null; weather_lng: number | null;
  show_map: boolean; show_map_units: boolean;
  history_hours: number; is_active: boolean;
}

interface AgencyRecord { id: number; name: string; agency_type: string; }

interface NamedLocation {
  id: number; name: string; latitude: number | null; longitude: number | null;
}

const emptyForm = {
  name: '', show_law: true, show_fire: true, show_ems: true,
  agency_ids: [] as number[], show_address: true, show_weather: true,
  weather_label: '', weather_lat: null as number | null, weather_lng: null as number | null,
  show_map: false, show_map_units: true,
  history_hours: 0,
};

export default function StatusBoardsPage() {
  const [boards, setBoards] = useState<StatusBoard[]>([]);
  const [agencies, setAgencies] = useState<AgencyRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StatusBoard | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState('');
  const [namedLocations, setNamedLocations] = useState<NamedLocation[]>([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  const load = () => {
    api.get('/status-boards').then((r) => setBoards(r.data)).catch(() => {});
    api.get('/agencies').then((r) => setAgencies(r.data)).catch(() => {});
    api.get('/locations').then((r) => setNamedLocations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const boardUrl = (token: string) => `${window.location.origin}/board/${token}`;

  const handleOpen = (b?: StatusBoard) => {
    if (b) {
      setEditing(b);
      setForm({
        name: b.name, show_law: b.show_law, show_fire: b.show_fire, show_ems: b.show_ems,
        agency_ids: b.agency_ids, show_address: b.show_address, show_weather: b.show_weather,
        weather_label: b.weather_label, weather_lat: b.weather_lat, weather_lng: b.weather_lng,
        show_map: b.show_map, show_map_units: b.show_map_units,
        history_hours: b.history_hours,
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setLocationQuery('');
    setOpen(true);
  };

  const pickNamedLocation = (id: number) => {
    const loc = namedLocations.find((l) => l.id === id);
    if (!loc || loc.latitude === null || loc.longitude === null) return;
    setForm((f) => ({ ...f, weather_label: loc.name, weather_lat: loc.latitude, weather_lng: loc.longitude }));
    setLocationQuery('');
  };

  type ResolvedLocation = { weather_label: string; weather_lat: number; weather_lng: number };

  const lookUpLocation = async (): Promise<ResolvedLocation | null> => {
    const query = locationQuery.trim();
    if (!query) return null;
    setLookingUp(true);
    try {
      const r = await api.post('/status-boards/resolve-location', { query });
      if (r.data.found) {
        const resolved: ResolvedLocation = {
          weather_label: r.data.label, weather_lat: r.data.lat, weather_lng: r.data.lng,
        };
        setForm((f) => ({ ...f, ...resolved }));
        setLocationQuery('');
        return resolved;
      }
      setToast('Could not find that ZIP code or address');
      return null;
    } catch {
      setToast('Location lookup failed');
      return null;
    } finally {
      setLookingUp(false);
    }
  };

  const clearWeatherLocation = () => {
    setForm((f) => ({ ...f, weather_label: '', weather_lat: null, weather_lng: null }));
  };

  const handleSave = async () => {
    let payload = form;
    // A typed-but-not-looked-up ZIP/address would otherwise be discarded.
    if (form.show_weather && locationQuery.trim()) {
      const resolved = await lookUpLocation();
      if (!resolved) return;
      payload = { ...form, ...resolved };
    }
    if (editing) {
      await api.patch(`/status-boards/${editing.id}`, payload);
    } else {
      await api.post('/status-boards', payload);
    }
    setOpen(false);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this status board? The board URL/token will stop working immediately.')) return;
    await api.delete(`/status-boards/${id}`);
    load();
  };

  const handleRegenerate = async (id: number) => {
    if (!window.confirm('Generate a new token? The current board URL will stop working immediately and must be re-opened on the screen.')) return;
    await api.post(`/status-boards/${id}/regenerate-token`);
    load();
  };

  const copyUrl = (token: string) => {
    navigator.clipboard.writeText(boardUrl(token)).then(() => setToast('Board URL copied')).catch(() => {});
  };

  const toggleAgency = (id: number) => {
    setForm((f) => ({
      ...f,
      agency_ids: f.agency_ids.includes(id)
        ? f.agency_ids.filter((a) => a !== id)
        : [...f.agency_ids, id],
    }));
  };

  const disciplineChips = (b: StatusBoard) => {
    const chips: string[] = [];
    if (b.show_law) chips.push('Law');
    if (b.show_fire) chips.push('Fire');
    if (b.show_ems) chips.push('EMS');
    return chips;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Status Boards</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Board</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only wall/bay screens (First Arriving-style). Each board has a friendly location name and a
        revocable token — open its URL once on the TV. Boards only show sanitized call data (no reporting-party
        name/phone or narrative).
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Location Name</TableCell>
              <TableCell>Disciplines</TableCell>
              <TableCell>Agencies</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={180} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {boards.map((b) => (
              <TableRow key={b.id}>
                <TableCell sx={{ fontWeight: 700 }}>{b.name}</TableCell>
                <TableCell>
                  {disciplineChips(b).map((c) => <Chip key={c} size="small" label={c} sx={{ mr: 0.5 }} />)}
                </TableCell>
                <TableCell>
                  {b.agency_ids.length === 0
                    ? <Chip size="small" label="All agencies" color="info" />
                    : b.agency_ids.map((id) => {
                        const a = agencies.find((x) => x.id === id);
                        return <Chip key={id} size="small" label={a ? a.name : `#${id}`} sx={{ mr: 0.5 }} />;
                      })}
                </TableCell>
                <TableCell>
                  {b.show_address ? <Chip size="small" label="Address" sx={{ mr: 0.5 }} /> : null}
                  {b.show_weather ? <Chip size="small" label={b.weather_lat !== null ? `Weather: ${b.weather_label || 'custom'}` : 'Weather'} sx={{ mr: 0.5, maxWidth: 220 }} /> : null}
                  {b.show_map ? <Chip size="small" label={b.show_map_units ? 'Map + Units' : 'Map'} color="primary" sx={{ mr: 0.5 }} /> : null}
                  {b.history_hours > 0 ? <Chip size="small" label={`History ${b.history_hours}h`} color="secondary" /> : null}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={b.is_active ? 'Active' : 'Inactive'} color={b.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Open board"><IconButton size="small" onClick={() => window.open(boardUrl(b.token), '_blank')}><OpenInNew fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Copy board URL"><IconButton size="small" onClick={() => copyUrl(b.token)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Regenerate token"><IconButton size="small" onClick={() => handleRegenerate(b.id)}><Autorenew fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(b)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(b.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {boards.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No status boards yet. Add one for each screen (e.g. "Fire Bay", "911 Center", "EMS").
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Status Board' : 'New Status Board'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth autoFocus label="Friendly Location Name" sx={{ mt: 1 }}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Fire Bay / 911 Center / EMS / Screen 1"
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Disciplines to show</Typography>
          <FormGroup row>
            <FormControlLabel control={<Checkbox checked={form.show_law} onChange={(e) => setForm({ ...form, show_law: e.target.checked })} />} label="Law" />
            <FormControlLabel control={<Checkbox checked={form.show_fire} onChange={(e) => setForm({ ...form, show_fire: e.target.checked })} />} label="Fire" />
            <FormControlLabel control={<Checkbox checked={form.show_ems} onChange={(e) => setForm({ ...form, show_ems: e.target.checked })} />} label="EMS" />
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Agencies</Typography>
          <Typography variant="caption" color="text.secondary">Leave all unchecked to show every agency.</Typography>
          <FormGroup sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1, p: 1, mt: 0.5 }}>
            {agencies.map((a) => (
              <FormControlLabel
                key={a.id}
                control={<Checkbox size="small" checked={form.agency_ids.includes(a.id)} onChange={() => toggleAgency(a.id)} />}
                label={<span>{a.name} <Typography component="span" variant="caption" color="text.secondary">({a.agency_type})</Typography></span>}
              />
            ))}
            {agencies.length === 0 && <Typography variant="caption" color="text.secondary">No agencies configured.</Typography>}
          </FormGroup>

          <Divider sx={{ my: 2 }} />
          <FormGroup>
            <FormControlLabel control={<Checkbox checked={form.show_address} onChange={(e) => setForm({ ...form, show_address: e.target.checked })} />} label="Show full address / named location" />
            <FormControlLabel control={<Checkbox checked={form.show_weather} onChange={(e) => setForm({ ...form, show_weather: e.target.checked })} />} label="Show weather panel (conditions + active alerts)" />
            <FormControlLabel control={<Checkbox checked={form.show_map} onChange={(e) => setForm({ ...form, show_map: e.target.checked })} />} label="Show map of active calls" />
            {form.show_map && (
              <FormControlLabel
                sx={{ ml: 3 }}
                control={<Checkbox checked={form.show_map_units} onChange={(e) => setForm({ ...form, show_map_units: e.target.checked })} />}
                label="…also plot logged-on units (AVL)"
              />
            )}
          </FormGroup>
          {form.show_map && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              A map plots exact incident coordinates — only enable this on screens in a secure,
              staff-only area, not a public lobby.
            </Typography>
          )}

          {form.show_weather && (
            <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Weather location (where this screen physically is)</Typography>
              {form.weather_lat !== null && form.weather_lng !== null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" color="success" label={form.weather_label || 'Custom location'} />
                  <Typography variant="caption" color="text.secondary">
                    {form.weather_lat.toFixed(4)}, {form.weather_lng.toFixed(4)}
                  </Typography>
                  <Button size="small" onClick={clearWeatherLocation}>Clear</Button>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Not set — this board uses the agency map center from System Config.
                </Typography>
              )}
              <TextField
                select fullWidth size="small" label="Use a named location (station)" value=""
                onChange={(e) => pickNamedLocation(Number(e.target.value))}
                helperText="Only named locations that have coordinates are listed."
              >
                {namedLocations.filter((l) => l.latitude !== null && l.longitude !== null).map((l) => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <TextField
                  fullWidth size="small" label="…or enter a ZIP code or address"
                  value={locationQuery} onChange={(e) => setLocationQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookUpLocation(); } }}
                  placeholder="65301 or 1 Fairgrounds Rd, Sedalia MO"
                />
                <Button variant="outlined" onClick={() => { void lookUpLocation(); }} disabled={lookingUp || !locationQuery.trim()}>
                  {lookingUp ? 'Looking up…' : 'Look Up'}
                </Button>
              </Box>
              <Typography
                variant="caption"
                color={locationQuery.trim() ? 'warning.main' : 'text.secondary'}
                sx={{ display: 'block', mt: 1 }}
              >
                {locationQuery.trim()
                  ? 'Press Look Up (or just Save) to resolve this to coordinates — a typed address is only stored once resolved.'
                  : 'Resolved to coordinates once when saved, so conditions and NWS warnings match this station rather than the agency-wide map center.'}
              </Typography>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Recent call history</Typography>
          <TextField
            type="number"
            size="small"
            label="Hours of history (0 = off)"
            value={form.history_hours}
            onChange={(e) => setForm({ ...form, history_hours: Math.max(0, Number(e.target.value) || 0) })}
            slotProps={{ htmlInput: { min: 0, max: 168 } }}
            sx={{ width: 220 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Shows a "Recent Calls" panel of closed/cancelled calls from the last X hours (this board only). Leave 0 to show only active calls.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
